import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Wand2 } from "lucide-react";

import { toast } from "sonner";

import { ImprovementReview } from "@/components/desk-improvements/improvement-review";
import { ImprovementRunsTable } from "@/components/desk-improvements/improvement-runs-table";
import { RunStatusBadge } from "@/components/desk-suggestions/run-status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { accessKeyErrorMessage } from "@/hooks/use-access-keys";
import {
  useDeskImprovementRun,
  useDeskImprovementRuns,
  DEFAULT_WINDOW_DAYS,
  useStartDeskImprovementRun,
} from "@/hooks/use-desk-improvements";
import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import { formatDateTime } from "@/lib/date-utils";
import { cn } from "@/lib/utils";

function Spinner({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label}>
      <Loader2
        aria-hidden
        className="text-muted-foreground h-5 w-5 animate-spin"
      />
    </div>
  );
}

const WINDOW_OPTIONS = [180, 120, 90, 60, 30, 14];

export const Route = createFileRoute("/desk-improvements")({
  component: DeskImprovementsPage,
});

function DeskImprovementsPage() {
  const [tenantId, setTenantId] = useState("");
  const [runId, setRunId] = useState("");
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);

  const {
    data: tenants,
    isLoading: tenantsLoading,
    failures: tenantFailures,
  } = useAllTenants();
  const {
    data: runs,
    isLoading: runsLoading,
    error: runsError,
  } = useDeskImprovementRuns(tenantId);
  const { data: run, error: runError } = useDeskImprovementRun(tenantId, runId);
  const startRun = useStartDeskImprovementRun();
  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;
  const runActive = runs?.some(
    (r) => r.status === "pending" || r.status === "running",
  );

  const handleStart = async () => {
    const startedFor = tenantId;
    try {
      const started = await startRun.mutateAsync({
        tenantId: startedFor,
        windowDays,
      });
      // the operator may have switched tenant while the request was in flight
      setRunId((current) =>
        tenantIdRef.current === startedFor ? started.id : current,
      );
      toast.success("Improve desks run started. It takes a few minutes.");
    } catch (error: unknown) {
      toast.error(accessKeyErrorMessage(error, "Failed to start the run"));
    }
  };

  const sortedTenants = useMemo(
    () =>
      [...(tenants ?? [])].sort((a, b) => a.company.localeCompare(b.company)),
    [tenants],
  );
  const selectedTenant = tenants?.find((t) => t.id === tenantId);

  const handleTenantChange = (id: string) => {
    setTenantId(id);
    setRunId("");
    setTenantPopoverOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Improve Desks</h1>
        <p className="text-muted-foreground">
          For a tenant already live on Harmony: read its recent tickets (180
          days by default), see how they were routed and corrected, and propose
          better desk and team descriptions plus configuration advice. Review
          and apply the ones you want. For a busy tenant whose run times out,
          read fewer days.
        </p>
      </div>
      <Separator />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Select Tenant</CardTitle>
            <CardDescription>
              Choose the tenant whose desks you want to improve.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Popover
              open={tenantPopoverOpen}
              onOpenChange={setTenantPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantPopoverOpen}
                  className="w-[300px] justify-between"
                >
                  <span className="truncate">
                    {tenantsLoading
                      ? "Loading..."
                      : selectedTenant
                        ? tenantLabel(selectedTenant)
                        : "Select tenant"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CellTenantsUnavailable failures={tenantFailures} />
                  <CommandInput placeholder="Search tenants..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No tenant found.</CommandEmpty>
                    <CommandGroup>
                      {sortedTenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenantOptionValue(tenant)}
                          onSelect={() => {
                            handleTenantChange(tenant.id);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              tenantId === tenant.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {tenantId && (
              <Select
                value={String(windowDays)}
                onValueChange={(value) => {
                  setWindowDays(Number(value));
                }}
              >
                <SelectTrigger
                  data-testid="improve-window"
                  aria-label="Tickets to read"
                  className="ml-3 inline-flex w-[170px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map((days) => (
                    <SelectItem key={days} value={String(days)}>
                      Last {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {tenantId && (
              <Button
                data-testid="start-improve"
                className="ml-3"
                disabled={
                  startRun.isPending ||
                  runsLoading ||
                  (Boolean(runsError) && !runs) ||
                  runActive
                }
                onClick={() => {
                  void handleStart();
                }}
              >
                {startRun.isPending ? (
                  <Loader2
                    aria-hidden
                    className="mr-1.5 h-4 w-4 animate-spin"
                  />
                ) : (
                  <Wand2 aria-hidden className="mr-1.5 h-4 w-4" />
                )}
                {runActive ? "A run is in progress" : "Improve desks"}
              </Button>
            )}
          </CardContent>
        </Card>

        {tenantId && (
          <Card>
            <CardHeader>
              <CardTitle>Improvement Runs</CardTitle>
              <CardDescription>
                Newest first. Pick a run to review its suggestions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <Spinner label="Loading improvement runs" />
              ) : runsError && !runs ? (
                <p className="text-destructive text-sm">
                  Failed to load improvement runs.
                </p>
              ) : runs && runs.length > 0 ? (
                <ImprovementRunsTable
                  runs={runs}
                  selectedRunId={runId}
                  onSelect={setRunId}
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  No improvement runs for this tenant yet.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {tenantId && runId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Suggestions {run && <RunStatusBadge status={run.status} />}
              </CardTitle>
              {run && (
                <CardDescription>
                  Started {formatDateTime(run.created_at)}
                  {run.finished_at &&
                    ` · finished ${formatDateTime(run.finished_at)}`}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!run ? (
                runError ? (
                  <p className="text-destructive text-sm">
                    Failed to load the improvement run.
                  </p>
                ) : (
                  <Spinner label="Loading improvement run" />
                )
              ) : run.status === "failed" ? (
                <p className="text-destructive text-sm">
                  This run failed{run.error ? `: ${run.error}` : "."}
                </p>
              ) : run.status === "pending" || run.status === "running" ? (
                <div
                  role="status"
                  className="text-muted-foreground flex items-center gap-2 text-sm"
                >
                  <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                  The run is reading the tenant&apos;s tickets and writing
                  suggestions (usually 3 to 15 minutes). This page updates when
                  it finishes.
                </div>
              ) : (
                <ImprovementReview key={run.id} tenantId={tenantId} run={run} />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
